import { useMemo } from "react";
import cookies from "utils/cookies";
import ability from "utils/abilit";
import { getFirstAccessibleRoute } from "utils/navigation";

const useAuth = () => {
  // Batch read cookies
  const token = cookies.get("token");
  const isAuthenticated = !!token;
  const user = cookies.get("user");
  const role = cookies.get("role");
  const company = cookies.get("company");
  const permissions = cookies.get("permissions");
  const userDetails = cookies.get("userDetails");

  // Memoize permissions processing
  const perms = useMemo(() => {
    if (permissions) {
      try {
        const permissionsObj = JSON.parse(permissions);
        return Object.keys(permissionsObj).map((key: string) => ({
          subject: key,
          permission: Object.keys(permissionsObj[key]).filter(
            (perm) => permissionsObj[key][perm]
          ),
        }));
      } catch (e) {
        console.error("Failed to parse permissions:", e);
        return [];
      }
    }
    return [];
  }, [permissions]);

  // Update ability with permissions
  useMemo(() => {
    ability.update(
      perms.map((p) => ({ subject: p.subject, action: p.permission }))
    );
  }, [perms]);

  // Login function
  const login = (data: {
    token: string;
    user: string;
    company: any;
    role: any;
    userDetails: any;
  }) => {
    cookies.set("token", data.token);
    cookies.set("user", data.user);
    cookies.set("role", data?.role ? JSON.stringify(data.role) : "");
    cookies.set("company", data?.company ? JSON.stringify(data.company) : "");
    cookies.set("permissions", JSON.stringify(data.role.rolePermissions));
    cookies.set("userDetails", data?.userDetails ? JSON.stringify(data.userDetails) : "");
    
    // Update abilities with new permissions
    const newPermissions = JSON.parse(JSON.stringify(data.role.rolePermissions));
    const newPerms = Object.keys(newPermissions).map((key: string) => ({
      subject: key,
      permission: Object.keys(newPermissions[key]).filter(
        (perm) => newPermissions[key][perm]
      ),
    }));
    
    ability.update(
      newPerms.map((p) => ({ subject: p.subject, action: p.permission }))
    );
    
    // Get the first accessible route and redirect there
    const firstAccessibleRoute = getFirstAccessibleRoute();
    window.location.href = firstAccessibleRoute;
  };

  // Logout function
  const logout = () => {
    cookies.remove("token");
    cookies.remove("user");
    cookies.remove("role");
    cookies.remove("company");
    cookies.remove("permissions");
    cookies.remove("userDetails");
    window.location.href = "/login";
  };

  return {
    token,
    isAuthenticated,
    user,
    role,
    company,
    permissions,
    userDetails,
    login,
    logout,
    getFirstAccessibleRoute,
  };
};

export default useAuth;
